import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ContextDocPreviewHandler } from '../../shared/context-doc-preview-handler';
import { DocumentPreviewModalComponent } from '../document-preview-modal/document-preview-modal';

/**
 * Mount point for the shared "preview a context doc" handler.
 *
 * The handler (`createContextDocPreviewHandler`) owns the open/close/
 * retry/openInNewTab state machine; this component is the presentation
 * glue that wires its `state()` signal into `<app-document-preview-modal>`.
 *
 * Every surface that previews context docs (assessment / document
 * drawers, edit-assessment, the main documents page + its context-doc
 * drawer) used to inline the same ~10-line modal binding block. With
 * this wrapper a new surface only does two things:
 *
 *   1. `readonly contextDocPreview = createContextDocPreviewHandler({…});`
 *   2. `<app-context-doc-preview [handler]="contextDocPreview" />`
 *
 * Keeping the binding behind a single component means the modal's
 * input/output contract can evolve in one place — no five-way ripple
 * through every consumer template.
 */
@Component({
  selector: 'app-context-doc-preview',
  imports: [DocumentPreviewModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (handler().state(); as p) {
      <app-document-preview-modal
        [isVisible]="true"
        [fileName]="p.fileName"
        [mimeType]="p.mimeType"
        [sourceUrl]="p.url"
        [subtitle]="p.subtitle"
        [loading]="p.loading"
        (closed)="handler().close()"
        (retry)="handler().retry()"
        (downloadRequested)="handler().openInNewTab()"
      />
    }
  `,
})
export class ContextDocPreviewComponent {
  readonly handler = input.required<ContextDocPreviewHandler>();
}
